/**
 * Things and money: coins and jade, picking up and equipping, the forge's hammer (a ringing
 * success, a dull clank and a hiss of steam for a failure), the trades' work, using a meal or a
 * medicine.
 */
import { BELL } from '../kit';
import { registerSfx } from '../registry';
import { chime, note, ROOT_D, woodblock } from '../voices';

registerSfx('econ.coin', { label: 'เหรียญกระทบ', cat: 'econ', vary: 0.06, gap: 0.06 }, (k) => {
  // A few coins jingling, each a small inharmonic ping.
  const n = 3 + Math.floor(k.r() * 3);
  for (let i = 0; i < n; i++)
    k.metal({
      f: k.range(2000, 3600),
      at: i * k.range(0.03, 0.07),
      dur: k.range(0.12, 0.3),
      vol: 0.05,
      partials: [
        [1, 1, 1],
        [2.3, 0.4, 0.6],
        [4.1, 0.2, 0.4],
      ],
    });
});

registerSfx('econ.jade', { label: 'หยกกระทบ', cat: 'econ', vary: 0.03, gap: 0.08, preview: 1.4 }, (k) => {
  // Jade on jade: a glassy, pure double clink.
  chime(k, { f: 2093, vol: 0.06, dur: 1 });
  chime(k, { f: 2637, at: 0.08, vol: 0.05, dur: 1.1 });
});

registerSfx('econ.pickup', { label: 'เก็บของ', cat: 'econ', vary: 0.04, gap: 0.08 }, (k) => {
  k.tone({ f: 600, f1: 1400, dur: 0.08, vol: 0.06 });
  chime(k, { f: note(ROOT_D * 4, 2), at: 0.05, vol: 0.04, dur: 0.6 });
});

registerSfx('econ.equip', { label: 'สวมใส่อุปกรณ์', cat: 'econ', vary: 0.05, gap: 0.1 }, (k) => {
  // Cloth settling, a buckle clicking shut, a ring of the fitting.
  k.noise({ dur: 0.18, vol: 0.14, attack: 0.03, f: 1200, f1: 700, q: 1 });
  k.click({ f: 2600, at: 0.14, vol: 0.2, q: 4 });
  k.metal({ f: 1900, at: 0.15, dur: 0.3, vol: 0.03 });
});

registerSfx(
  'econ.forge.ok',
  { label: 'ตีเหล็กสำเร็จ', cat: 'econ', vary: 0.02, gap: 0.3, preview: 2 },
  (k) => {
    // Hammer on the anvil, the blade ringing bright, and a rising chime of success.
    k.thump({ f0: 300, f1: 140, dur: 0.06, vol: 0.3 });
    k.metal({ f: 880, dur: 0.9, vol: 0.12 });
    k.metal({ f: 1320, at: 0.01, dur: 0.6, vol: 0.06, partials: BELL });
    [0, 2, 4].forEach((d, i) => chime(k, { f: note(ROOT_D * 4, d), at: 0.3 + i * 0.09, vol: 0.05, dur: 1 }));
  },
);

registerSfx(
  'econ.forge.fail',
  { label: 'ตีเหล็กล้มเหลว', cat: 'econ', vary: 0.02, gap: 0.3, preview: 1.6 },
  (k) => {
    // A dull clank, a hiss of steam, a low thud.
    k.metal({ f: 410, dur: 0.3, vol: 0.08 });
    k.thump({ f0: 160, f1: 60, dur: 0.2, vol: 0.3 });
    k.noise({
      at: 0.1,
      dur: 0.9,
      vol: 0.12,
      attack: 0.05,
      type: 'highpass',
      f: 3500,
      f1: 2000,
      linear: true,
    });
    k.tone({ f: 330, f1: 196, at: 0.15, dur: 0.6, vol: 0.04 });
  },
);

registerSfx('econ.gather', { label: 'เก็บเกี่ยว', cat: 'econ', vary: 0.08, gap: 0.2 }, (k) => {
  k.noise({ color: 'crackle', dur: 0.2, vol: 0.4, type: 'highpass', f: 1800 });
  k.noise({ dur: 0.18, vol: 0.1, attack: 0.03, f: 2500, q: 1 });
  k.pluck({ f: note(ROOT_D * 2, 2), at: 0.1, dur: 0.5, vol: 0.06 });
});

registerSfx('econ.craft', { label: 'ประดิษฐ์ของ', cat: 'econ', vary: 0.04, gap: 0.2 }, (k) => {
  woodblock(k, { f: 820, vol: 0.12 });
  woodblock(k, { f: 900, at: 0.12, vol: 0.12 });
  chime(k, { f: note(ROOT_D * 4, 4), at: 0.25, vol: 0.04 });
});

registerSfx('econ.use', { label: 'ใช้ยา · กินอาหาร', cat: 'econ', vary: 0.08, gap: 0.3 }, (k) => {
  // A gulp: two low bubbly glides.
  k.tone({ f: 220, f1: 420, dur: 0.1, vol: 0.12 });
  k.tone({ f: 200, f1: 380, at: 0.16, dur: 0.1, vol: 0.1 });
  k.noise({ color: 'brown', dur: 0.3, vol: 0.12, type: 'lowpass', f: 500 });
});

registerSfx('econ.work.start', { label: 'เริ่มงาน', cat: 'econ', vary: 0.05, gap: 0.3 }, (k) => {
  woodblock(k, { f: 700, vol: 0.1 });
});

registerSfx('econ.work.done', { label: 'งานเสร็จ', cat: 'econ', vary: 0, gap: 0.3 }, (k) => {
  [2, 4].forEach((d, i) => k.pluck({ f: note(ROOT_D * 2, d), at: i * 0.08, dur: 0.7, vol: 0.09 }));
});
